import { useMemo } from 'react'

import { CategoryCard } from '@hhgtech/hhg-components/components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, Grid, MediaQuery } from '@mantine/core'
import { useIntl } from 'react-intl'

import { NextImageWrap } from 'components/atoms/imageWrap'
import { CarouselOrListingBlockWrapper } from 'components/organisms/serviceConnections/carouselOrListingBlockWrapper'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { DEFAULT_RESULT_IMG, LOCALE_SPECS } from 'interfaces/constants'
import { CategoriesLayout, Category } from 'interfaces/types'

import { StyledCategorySubCategory } from './index.styled'
import useStyles from './index.styles'

type Props = {
  isMobile: boolean
  subCategories: Array<Category>
  categoryName: string
}

export const CategorySubCategory = ({
  isMobile,
  subCategories,
  categoryName,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const {
    locale,
    router: { defaultLocale = 'en-US' },
  } = useRouterExtend()

  const { classes, theme } = useStyles(undefined, {
    name: 'ArticleCardList__Categories',
  })

  const categoriesType = (LOCALE_SPECS[locale || defaultLocale]
    .CATEGORIES_TYPE || 'long') as CategoriesLayout

  const listLength = useMemo(() => subCategories.length, [subCategories])
  const perSlide = useMemo(
    () => (isMobile ? (categoriesType === 'long' ? 4 : 6) : 1),
    [isMobile, categoriesType],
  )
  const slideGroup = useMemo(
    () =>
      new Array(Math.ceil(listLength / perSlide))
        .fill('')
        .map((_i, idx) =>
          subCategories.slice(idx * perSlide, idx * perSlide + perSlide),
        ),
    [isMobile],
  )

  const [column] = useMemo(() => {
    let _column: 1 | 6 | 5 | 4 | 3 | 2

    if (categoriesType === 'long') {
      _column = 1
    } else {
      if (listLength === 1) {
        _column = 1
      } else if (listLength === 2 || listLength === 4) {
        _column = 2
      } else {
        _column = 3
      }
    }

    return [_column]
  }, [categoriesType, listLength])

  return (
    <StyledCategorySubCategory
      data-layout-type={categoriesType}
      data-type-mobile={isMobile}
    >
      {subCategories.length > 0 && (
        <div className="wrapper">
          <div className="text">
            <Text size="h3">
              {f(
                { id: 'categoryPage.subCategories' },
                {
                  name: categoryName,
                },
              )}
            </Text>
          </div>
          <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
            <Box>
              <CarouselOrListingBlockWrapper
                lengthToCarouselMobile={2}
                showFullSlide
                carouselSetting={{
                  loop: false,
                  withIndicators: true,
                }}
              >
                <CarouselOrListingBlockWrapper.Slider>
                  {slideGroup.map((item, itemIdx) => (
                    <Grid
                      justify={
                        slideGroup.length === 1 && item.length === 1
                          ? 'center'
                          : undefined
                      }
                      gutterMd={24}
                      gutterXs={0}
                      key={itemIdx}
                      sx={{
                        [theme.fn.smallerThan('sm')]: {
                          height: 'auto !important',
                        },
                      }}
                    >
                      {item.map((category, idx) => (
                        <Grid.Col span={12 / column} key={idx}>
                          <Box
                            key={idx}
                            className={classes.slider}
                            sx={{
                              '.category-card': {
                                display: 'block',
                                width: 'auto !important',
                              },
                            }}
                          >
                            <CategoryCard
                              key={category.termId}
                              category={category}
                              dataEventCategory="Recommended Topics"
                              dataEventAction="Click"
                              dataEventLabel={`https://${domainLocales[locale]}${category.permalinkDecode}`}
                              data-type-mobile={isMobile}
                              className="category-card"
                              imgBanner={
                                <NextImageWrap
                                  layout="responsive"
                                  loading="lazy"
                                  objectFit="contain"
                                  src={category.iconUrl || DEFAULT_RESULT_IMG}
                                  width={
                                    isMobile
                                      ? categoriesType === 'long'
                                        ? 48
                                        : 73
                                      : 100
                                  }
                                  height={
                                    isMobile
                                      ? categoriesType === 'long'
                                        ? 48
                                        : 63
                                      : 100
                                  }
                                  alt={category.categoryName}
                                  style={{
                                    objectFit: 'contain',
                                  }}
                                />
                              }
                              label={category.categoryName}
                              href={category.permalink}
                            />
                          </Box>
                        </Grid.Col>
                      ))}
                    </Grid>
                  ))}
                </CarouselOrListingBlockWrapper.Slider>
              </CarouselOrListingBlockWrapper>
            </Box>
          </MediaQuery>
          <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
            <Box>
              <CarouselOrListingBlockWrapper
                lengthToCarouselDesktop={7}
                showFullSlide
              >
                <CarouselOrListingBlockWrapper.Slider
                  styles={{
                    itemWrapper: {
                      [theme.fn.largerThan('sm')]: {
                        ...(listLength < 6 && {
                          width: 170,
                          flex: 'none',
                        }),
                      },
                    },
                  }}
                >
                  {subCategories.map((category, idx) => (
                    <Box key={idx} className={classes.slider}>
                      <CategoryCard
                        key={category.termId}
                        category={category}
                        dataEventCategory="Recommended Topics"
                        dataEventAction="Click"
                        dataEventLabel={`https://${domainLocales[locale]}${category.permalinkDecode}`}
                        data-type-mobile={isMobile}
                        className="category-card"
                        imgBanner={
                          <NextImageWrap
                            loading="lazy"
                            src={category.iconUrl || DEFAULT_RESULT_IMG}
                            width={
                              isMobile
                                ? categoriesType === 'long'
                                  ? 48
                                  : 73
                                : 100
                            }
                            height={
                              isMobile
                                ? categoriesType === 'long'
                                  ? 48
                                  : 63
                                : 100
                            }
                            alt={category.categoryName}
                            style={{
                              objectFit: 'contain',
                            }}
                          />
                        }
                        label={category.categoryName}
                        href={category.permalink}
                      />
                    </Box>
                  ))}
                </CarouselOrListingBlockWrapper.Slider>
              </CarouselOrListingBlockWrapper>
            </Box>
          </MediaQuery>
        </div>
      )}
    </StyledCategorySubCategory>
  )
}
